import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage';
import { Store } from '@ngrx/store';
import { LoadingService } from './loading.service';
import { PopupService } from './popup.service';
import { SortAndFilterService } from './sort-and-filter.service';
import { UtilService } from './util.services';
import { LocalStorageKeys } from '../enums/local-storage-keys.enum';
import { IAuthState } from 'src/modules/authentication/+store/auth.state';
import { getUserInfoAction } from 'src/modules/authentication/+store/actions/auth.actions';
import { resetTalentDashboardStoreAction } from 'src/modules/talent-dashboard/+store/actions/talent-dashboard.actions';
import { TalentSyncService } from 'src/modules/talent-dashboard/services/talent-sync.service';

const PRESERVE_KEYS = [
  'token',
  'user',
  'staffyLogo',
  'staffyIcon',
  'staffyTextLogo',
  'staffyIconOrange',
  'expertise',
  'language',
  'shiftInProgress',
  LocalStorageKeys.HAS_COMPLETED_INTRODUCTORY_STEPS
];

@Injectable()
export class CacheResetService {
  constructor(
    private storage: Storage,
    private popupService: PopupService,
    private loadingService: LoadingService,
    private sortAndFilterService: SortAndFilterService,
    private utilService: UtilService,
    private talentSyncService: TalentSyncService,
    private store: Store<IAuthState>
  ) {}

  async resetCacheAndRelaunch() {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      await this.popupService.showPopup(
        'No Internet Connection',
        'You need to be online to reset the cache. Please reconnect and try again.'
      );
      return;
    }

    const confirmed = await this.confirm();
    if (!confirmed) {
      return;
    }

    const loader = await this.loadingService.showLoading().toPromise();
    try {
      const preserved: { [key: string]: any } = {};
      for (const key of PRESERVE_KEYS) {
        preserved[key] = await this.storage.get(key);
      }

      await this.storage.clear();

      for (const key of PRESERVE_KEYS) {
        const value = preserved[key];
        if (value !== null && value !== undefined) {
          await this.storage.set(key, value);
        }
      }

      localStorage.setItem('filterOutClients', JSON.stringify([]));
      localStorage.removeItem('camera_permission_attempts');
      this.sortAndFilterService.resetAllFilters();
      this.sortAndFilterService.resetAllSorts();

      this.store.dispatch(resetTalentDashboardStoreAction());
      this.store.dispatch(getUserInfoAction());

      // The cache is now actually cleared, so confirm to the user immediately
      // rather than blocking the popup on the (slow) re-sync below.
      await this.loadingService.hideLoading(loader);
      await this.showSuccessPopup();

      // Re-sync upcoming shifts in the background so the header/footer counts
      // (cleared by resetTalentDashboardStoreAction) and the available-shifts
      // list repopulate without making the user wait. If it fails the cache is
      // already clean, so the app re-syncs on the next navigation anyway.
      this.talentSyncService.sync()
        .then(() => this.utilService.cacheReset$.next())
        .catch(e => console.log('post-reset sync failed', e));
    } catch (e) {
      console.log('Cache reset failed', e);
      await this.loadingService.hideLoading(loader);
    }
  }

  private confirm(): Promise<boolean> {
    return new Promise(resolve => {
      this.popupService.showModal(
        {
          heading: 'Reset Cache',
          message: "This will refresh your local data. You'll stay signed in.",
          btn: 'Reset',
          rejectBtnText: 'Cancel',
          navigateRoute: null,
          imgURL: 'assets/images/notice.png',
          blurBackground: true
        },
        () => resolve(true),
        () => resolve(false)
      );
    });
  }

  private showSuccessPopup(): Promise<void> {
    return new Promise(resolve => {
      this.popupService.showModal(
        {
          heading: 'Success',
          message: 'Cache cleared successfully',
          btn: 'OK',
          navigateRoute: null,
          imgURL: 'assets/images/hands-up.png',
          blurBackground: true
        },
        () => resolve(),
        () => resolve()
      );
    });
  }
}
